import { Stack, Typography } from '@mui/material';
import { formatToCNPJ } from 'brazilian-values';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { getFrequencyI18n } from '@woovi/shared';
import { obscureCNPJ } from '@woovi/utils';

import type { PaymentPixAutomaticTransactionInfo_subscription$key } from './__generated__/PaymentPixAutomaticTransactionInfo_subscription.graphql.ts';
import { PixAutomaticInfoLabel } from './PixAutomaticInfoLabel.tsx';

type PaymentPixAutomaticInfoProps = {
  pixAutomatic: PaymentPixAutomaticTransactionInfo_subscription$key;
};

export const PaymentPixAutomaticTransactionInfo = (
  props: PaymentPixAutomaticInfoProps,
): ReactNode => {
  const { t } = useTranslation();
  const pixAutomatic =
    useFragment<PaymentPixAutomaticTransactionInfo_subscription$key>(
      graphql`
        fragment PaymentPixAutomaticTransactionInfo_subscription on PaymentSubscription {
          comment
          frequency
          company {
            projectType
            nameFriendly
            taxID {
              taxID
            }
          }
          pixRecurring {
            recurrencyId
          }
        }
      `,
      props.pixAutomatic,
    );

  return (
    <Stack gap={1} marginY={2}>
      <Typography variant='h6' fontWeight='bold' textAlign='center'>
        {t('Pix Automatic Details')}
      </Typography>
      <PixAutomaticInfoLabel
        title={t('Description')}
        content={pixAutomatic.comment || '-'}
      />
      <PixAutomaticInfoLabel
        title={t('Frequency')}
        content={getFrequencyI18n({ key: pixAutomatic.frequency as string, t })}
      />
      <PixAutomaticInfoLabel
        title={t('Addressee')}
        content={
          <Stack alignItems='center'>
            <Typography sx={{ fontSize: '16px', overflowWrap: 'anywhere' }}>
              {pixAutomatic.company?.nameFriendly}
            </Typography>
            <Typography sx={{ fontSize: '16px', overflowWrap: 'anywhere' }}>
              CNPJ:{' '}
              {obscureCNPJ(
                formatToCNPJ(pixAutomatic.company?.taxID?.taxID || ''),
              )}
            </Typography>
          </Stack>
        }
      />

      <PixAutomaticInfoLabel
        title={t('Recurrency ID')}
        content={pixAutomatic.pixRecurring?.recurrencyId || '-'}
      />
    </Stack>
  );
};
